import React, { useCallback, useEffect } from "react";

import cn from "classnames";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import Activity from "@mapmycustomers/shared/types/entity/Activity";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";
import { SearchField } from "@mapmycustomers/ui";

import useView from "@app/component/view/utils/useView";
import { activityListViewAnalytics } from "@app/scene/activity/activityTableAnalytics";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import styles from "./QuickSearch.module.scss";

interface Props {
  className?: string;
}

const QuickSearch: React.FC<Props> = ({ className }) => {
  const intl = useIntl();
  const {
    updateFilterModel,
    viewState: { filter },
  } = useView<Activity>();
  const updateFilters = useCallback(
    (value: string) =>
      updateFilterModel({
        quickFilter: value ? { operator: FilterOperator.CONTAINS, value } : undefined,
      }),
    [updateFilterModel]
  );
  const [value, setValue, resetValue] = useStateWithDebouncedListener([updateFilters, 500], "");
  const handleEnterPress = useDynamicCallback(() => {
    updateFilters(value);
  });
  useEffect(() => {
    if (
      filter.quickFilter &&
      isSimpleCondition(filter.quickFilter) &&
      filter.quickFilter.operator === FilterOperator.CONTAINS
    ) {
      resetValue(filter.quickFilter.value);
    } else {
      resetValue("");
    }
  }, [filter, resetValue]);

  const handleSearchClickAnalytics = useCallback(() => {
    activityListViewAnalytics.clicked(["Search"]);
  }, []);

  return (
    <SearchField
      className={cn(styles.container, className)}
      onChange={setValue}
      onClick={handleSearchClickAnalytics}
      onPressEnter={handleEnterPress}
      placeholder={intl.formatMessage({
        id: "activityList.search.placeholder",
        defaultMessage: "Search",
        description: "Activity list search placeholder",
      })}
      value={value}
    />
  );
};

export default QuickSearch;
